import {ChangeDetectionStrategy, Component} from '@angular/core';
import {TableCardDefinition} from '@store/aviary/aviary.adapter';
import {Checkout, CHECKOUT_CLAIM_TYPE_CHIP_COLORS, CheckoutClaim, DISPLAYABLE_CHECKOUT_CLAIM_TYPE} from '@store/checkouts/checkouts.types';
import {CheckoutsSelectors} from '@store/checkouts/checkouts.selectors';
import {TableCardComponent} from '@aviary/components/table-card/table-card.component';
import {CombinedCoverTitleDetailComponent} from '@lib/components/combined-cover-title-detail/combined-cover-title-detail.component';
import {ALWAYS, CURRENT_ROW_OBJECT} from '@aviary/components/table/table.component.types';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {MelHintLinkComponent} from '@lib/components/mel-hint-link/mel-hint-link.component';
import {ClaimsMobileColumnComponent} from '@app/my-account/checked-out/claims/claims-mobile-column.component';
import {CheckoutsActions} from '@store/checkouts/checkouts.actions';
import {LinkButtonComponent} from '@aviary/buttons/link-button/link-button.component';
import {ButtonRouteNameDirective} from '@lib/directives/button-route-name/button-route-name.directive';

@Component({
  selector: 'px-checked-out',
  host: {
    class: 'flex flex-col gap-y-7'
  },
  template: `
    <mx-table-card [definition]="checkoutsTableCardDefinition">
      <div footer class="flex flex-row justify-between items-center">
        <px-mel-hint-link/>
        <mx-link-button label="View Borrowing History" endIcon="arrow_forward" routeName="BORROWING_HISTORY"/>
      </div>
    </mx-table-card>

    @if (claimsSelectors.hasClaims | select) {
      <mx-table-card [definition]="claimsTableCardDefinition"/>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TableCardComponent, SelectPipe, MelHintLinkComponent, LinkButtonComponent, ButtonRouteNameDirective]
})
export class CheckedOutComponent {

  public readonly claimsSelectors = CheckoutsSelectors.Claims;

  public readonly checkoutsTableCardDefinition: TableCardDefinition<Checkout, typeof CheckoutsSelectors.CheckedOut> = {
    leftHeaderContent: {
      value: 'Checked Out'
    },
    actions: [
      {
        label: 'Renew All',
        clickActionCreator: CheckoutsActions.showRenewAllItems,
        enabled: CheckoutsSelectors.CheckedOut.hasCheckouts
      }
    ],
    table: {
      defaultSelectorContainer: CheckoutsSelectors.CheckedOut,
      dataSource: 'checkouts',
      responsiveColumnsDefinition: {
        verticalActionsSm: true,
        responsiveHideHeaderRow: true,
        columnNamesSm: ['itemSm'],
        columnNamesMd: ['dueDate', 'itemMd', 'checkoutTime'],
        columnNamesLg: ['dueDate', 'itemLg', 'itemMediaType', 'checkoutTime']
      },
      columns: [
        {
          type: 'text',
          value: 'dueDateFor',
          header: 'Due Date',
          format: 'DATE_ONLY',
          cssClasses: {
            'text-error': 'overDueFor',
            'text-title-medium': 'overDueFor'
          },
          headerCssClasses: 'w-[7rem]',
          sortable: true,
        },
        {
          type: 'component',
          columnName: 'itemLg',
          header: 'Item',
          componentClass: CombinedCoverTitleDetailComponent,
          componentProperties: {
            catalogRecord: 'buildCatalogRecordFor',
            detail1: 'itemBarcodeFor',
          },
          sortable: true,
          sortKey: 'title'
        },
        {
          type: 'component',
          columnName: 'itemMd',
          header: 'Item',
          componentClass: CombinedCoverTitleDetailComponent,
          componentProperties: {
            catalogRecord: 'buildCatalogRecordFor',
            detail1: 'itemBarcodeFor',
            detail2: 'itemMediaTypeFor',
          },
          sortable: true,
          sortKey: 'title'
        },
        {
          type: 'component',
          columnName: 'itemSm',
          componentClass: CombinedCoverTitleDetailComponent,
          componentProperties: {
            catalogRecord: 'buildCatalogRecordFor',
            showCover: false,
            detail1: 'itemBarcodeFor',
            detail2: 'itemMediaTypeFor',
            date1Label: 'Due Date',
            date1: 'dueDateFor',
            date1Error: 'overDueFor',
            date2Label: 'Check Out Date',
            date2: 'checkoutTimeFor',
          }
        },
        {
          type: 'text',
          value: 'itemMediaTypeFor',
          header: 'Material Type',
          headerCssClasses: 'w-[10rem]',
          truncate: true,
          sortable: true,
          sortKey: 'materialType'
        },
        {
          type: 'text',
          value: 'checkoutTimeFor',
          header: 'Check Out Date',
          format: 'DATE_ONLY',
          headerCssClasses: 'w-[10rem]',
          sortable: true,
        }
      ],
      actionIconButtons: [
        {
          iconName: 'autorenew',
          tooltip: 'Renew Item',
          cssClasses: {'material-icons-outlined': ALWAYS},
          clickActionCreator: CheckoutsActions.showRenewItem
        },
        {
          iconName: 'how_to_vote',
          tooltip: 'File Claim',
          cssClasses: {'material-icons-outlined': ALWAYS},
          clickActionCreator: CheckoutsActions.showFileClaim
        }
      ]
    }
  };

  public readonly claimsTableCardDefinition: TableCardDefinition<CheckoutClaim, typeof CheckoutsSelectors.Claims> = {
    leftHeaderContent: {
      value: 'Claims',
      count: CheckoutsSelectors.Claims.numberOfClaims
    },
    table: {
      defaultSelectorContainer: CheckoutsSelectors.Claims,
      dataSource: 'claims',
      includePaginationControls: false,
      responsiveColumnsDefinition: {
        responsiveHideHeaderRow: true,
        omitActionsSm: true,
        columnNamesSm: ['itemSm', 'viewAndStatus'],
        columnNamesMd: ['created', 'itemMd', 'type'],
        columnNamesLg: ['created', 'itemLg', 'itemMediaType', 'type']
      },
      columns: [
        {
          type: 'text',
          value: 'createdFor',
          header: 'Submitted',
          format: 'DATE_ONLY',
          headerCssClasses: 'w-[7rem]',
        },
        {
          type: 'component',
          columnName: 'itemLg',
          header: 'Item',
          componentClass: CombinedCoverTitleDetailComponent,
          componentProperties: {
            catalogRecord: 'buildCatalogRecordFor',
            detail1: 'itemBarcodeFor',
          }
        },
        {
          type: 'component',
          columnName: 'itemMd',
          header: 'Item',
          componentClass: CombinedCoverTitleDetailComponent,
          componentProperties: {
            catalogRecord: 'buildCatalogRecordFor',
            detail1: 'itemBarcodeFor',
            detail2: 'itemMediaTypeFor',
          }
        },
        {
          type: 'component',
          columnName: 'itemSm',
          componentClass: CombinedCoverTitleDetailComponent,
          componentProperties: {
            catalogRecord: 'buildCatalogRecordFor',
            showCover: false,
            detail1: 'itemBarcodeFor',
            detail2: 'itemMediaTypeFor',
            date1Label: 'Submitted',
            date1: 'createdFor',
          }
        },
        {
          type: 'component',
          columnName: 'viewAndStatus',
          headerCssClasses: 'w-[12rem]',
          componentClass: ClaimsMobileColumnComponent,
          componentProperties: {
            claim: CURRENT_ROW_OBJECT
          }
        },
        {
          type: 'text',
          value: 'itemMediaTypeFor',
          header: 'Material Type',
          headerCssClasses: 'w-[10rem]',
          truncate: true
        },
        {
          type: 'statusChip',
          value: 'typeFor',
          header: 'Status',
          headerCssClasses: 'w-[13rem]',
          statusChipColors: CHECKOUT_CLAIM_TYPE_CHIP_COLORS,
          displayableStatusNames: DISPLAYABLE_CHECKOUT_CLAIM_TYPE,
        },
      ],
      actionIconButtons: [
        {
          iconName: 'text_snippet',
          tooltip: 'Details',
          cssClasses: {'material-icons-outlined': ALWAYS},
          clickActionCreator: CheckoutsActions.showViewClaim
        }
      ]
    }
  };
}
